// src/slices/ticketReturnWarehouseReport/thunk.ts
// Async thunks cho US-019 — Lưu trữ vật lý & báo cáo nhanh thu hồi vé ế.
// Bám đúng TicketReturnReconciliationController: query PascalCase, report dùng param `PeriodId`,
// warehouse-location trả về `long`.

import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  getTicketReturnReconciliationsApi,
  setTicketReturnWarehouseLocationApi,
  getTicketReturnReportApi,
  exportTicketReturnReportApi,
} from 'helpers/fakebackend_helper';
import { RECONCILIATION_STATUS_QUERY } from 'slices/ticketReturnReconciliation/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_return_warehouse_report_messages');

const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

/**
 * Danh sách phiếu đã thẩm định — nguồn để gán vị trí kho lưu trữ.
 * LƯU Ý: endpoint CHỈ hỗ trợ lọc `Status` + `AgentId` (không có IssuancePlanId), nên bộ lọc
 * "Kỳ phát hành" trên trang chỉ áp dụng cho phần Báo cáo.
 */
export const fetchApprovedReconciliations = createAsyncThunk(
  'TicketReturnWarehouseReport/fetchApprovedList',
  async (
    params: { agentId?: number | string; pageIndex?: number; pageSize?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await getTicketReturnReconciliationsApi(
        cleanParams({
          Status: RECONCILIATION_STATUS_QUERY.DaThamDinh,
          AgentId: params.agentId,
          PageIndex: params.pageIndex,
          PageSize: params.pageSize,
        }),
      );
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
    }
  },
);

// Gán vị trí kho lưu trữ vật lý — API chỉ trả về id, nơi gọi phải refetch danh sách
export const setWarehouseLocation = createAsyncThunk(
  'TicketReturnWarehouseReport/setWarehouseLocation',
  async (
    { id, warehouseLocation }: { id: number; warehouseLocation: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await setTicketReturnWarehouseLocationApi(id, { warehouseLocation });
      return (response as any)?.data ?? response ?? id;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_luu_vi_tri_kho_message'));
    }
  },
);

// Báo cáo nhanh — trả về MẢNG PHẲNG (không phân trang, không kèm summary)
export const fetchTicketReturnReport = createAsyncThunk(
  'TicketReturnWarehouseReport/fetchReport',
  async (
    params: { issuancePlanId?: number | string; agentId?: number | string },
    { rejectWithValue },
  ) => {
    try {
      const response = await getTicketReturnReportApi(
        // Tên param là `PeriodId` dù giá trị truyền vào chính là IssuancePlanId (README mục 10 — B2)
        cleanParams({ PeriodId: params.issuancePlanId, AgentId: params.agentId }),
      );
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_bao_cao_thu_message'));
    }
  },
);

/**
 * Export báo cáo — pattern blob-download giống slices/sinhBoSoQr/thunk.ts#exportQrCsv.
 * LƯU Ý: BE hiện luôn trả CSV (UTF-8 có BOM) cho cả 3 format vì project chưa có thư viện sinh
 * xlsx/pdf (ExportTicketReturnReportQueryHandler). Vẫn gửi đúng `format` người dùng chọn để
 * không phải sửa contract khi BE nâng cấp, nhưng đặt phần mở rộng file là .csv cho khớp nội dung
 * thật — nếu đặt .xlsx, Excel sẽ báo file hỏng.
 */
export const exportTicketReturnReport = createAsyncThunk(
  'TicketReturnWarehouseReport/exportReport',
  async (
    params: {
      issuancePlanId?: number | string;
      agentId?: number | string;
      format: 'xlsx' | 'pdf' | 'csv';
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await exportTicketReturnReportApi(
        cleanParams({
          PeriodId: params.issuancePlanId,
          AgentId: params.agentId,
          format: params.format,
        }),
      );
      const blobContent = (response as any)?.data || response;

      const downloadUrl = window.URL.createObjectURL(new Blob([blobContent]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute(
        'download',
        `bao_cao_thu_hoi_ve_e_${moment().format('YYYYMMDD_HHmmss')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(st('tai_xuong_bao_cao_thanh_cong_message'));
      return true;
    } catch (error: any) {
      const message = error?.message || st('khong_the_tai_xuong_bao_cao_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);
